import { z } from 'zod';
import { scopeTypeSchema, type LoginInput } from '@eombor/contracts';
import type { ApiClient } from '../../../shared/api/client.js';

/**
 * Backend javoblarining sxemasi. Ular `@eombor/contracts` dagi
 * `AuthTokens` / `AuthUser` / `GrantedPermission` turlarining ish vaqtidagi
 * tekshiruvi — tur bor-u, sxema yo'q bo'lsa, noto'g'ri javob UI ichida
 * `undefined` bo'lib tarqaladi.
 */
export const authUserSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  username: z.string(),
  jshshir: z.string(),
  isSuperadmin: z.boolean(),
  mustChangePassword: z.boolean(),
  telegramLinked: z.boolean(),
});

export type SessionUser = z.infer<typeof authUserSchema>;

export const authTokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  user: authUserSchema,
});

export type AuthTokensDto = z.infer<typeof authTokensSchema>;

/** `GET /auth/me` — foydalanuvchi maydonlari + hisoblangan ruxsatlar. */
export const grantedPermissionSchema = z.object({
  permission: z.string(),
  scope: z.object({ type: scopeTypeSchema, id: z.string().nullable() }),
  /* `ORG_UNIT` doirasidagi korxonaning MTU si. `optional()` — eski
     server bu maydonni yubormaydi va sxema uni qat'iy talab qilsa,
     javob butunlay rad etilib, odam tizimga kira olmasdi. */
  scopeMtuId: z.string().nullable().optional(),
  /* Doiradagi korxonaning O'Z ombori bormi. `optional()` — yuqoridagi
     bilan bir sabab: eski server bu maydonni yubormaydi. */
  scopeHasOwnWarehouse: z.boolean().optional(),
});

export const meSchema = authUserSchema.extend({
  permissions: z.array(grantedPermissionSchema),
});

export type MeDto = z.infer<typeof meSchema>;

/**
 * Telegram orqali kirish javoblari.
 *
 * `pollSecret` FAQAT shu javobda keladi va brauzerdan chiqmaydi: havoladagi
 * `nonce` Telegramga ochiq ketgani uchun, natijani so'rovni boshlagan
 * brauzergina ola olishi kerak.
 */
export const telegramStartSchema = z.object({
  nonce: z.string(),
  pollSecret: z.string(),
  deepLink: z.string(),
  /** Brauzerda ko'rsatiladigan raqam — odam uni botda tanlaydi. */
  matchCode: z.string(),
  expiresAt: z.string(),
});

export type TelegramStartDto = z.infer<typeof telegramStartSchema>;

export const telegramStatusSchema = z.object({
  state: z.enum(['PENDING', 'APPROVED', 'EXPIRED', 'REJECTED']),
  tokens: authTokensSchema.optional(),
  reason: z.string().optional(),
});

export type TelegramStatusDto = z.infer<typeof telegramStatusSchema>;

/**
 * Serverdagi eski TTL (`captcha.service.ts` dagi `TTL_SECONDS`) — zaxira qiymat.
 *
 * `expiresIn` OPTIONAL: web va API alohida deploy qilinadi, ya'ni yangi web
 * eski API bilan uchrashishi mumkin. Majburiy qilinsa, sxema tekshiruvi
 * yiqilib, CAPTCHA butunlay ishlamay qolardi — tuzatmoqchi bo'lgan
 * nosozlikdan ham battar.
 */
const DEFAULT_CAPTCHA_TTL = 120;

export const captchaSchema = z.object({
  id: z.string(),
  svg: z.string(),
  expiresIn: z.number().positive().optional(),
});

/** Chaqiruvchi uchun `expiresIn` HAR DOIM bor — zaxira qiymat `createAuthApi` da qo'yiladi. */
export type CaptchaDto = z.infer<typeof captchaSchema> & { expiresIn: number };

/** Tekshiruv natijasi — kirish so'rovi kodni emas, shu chiptani olib boradi. */
export const captchaTicketSchema = z.object({ token: z.string(), expiresAt: z.string() });
export type CaptchaTicketDto = z.infer<typeof captchaTicketSchema>;

export interface AuthApi {
  captcha: () => Promise<CaptchaDto>;
  captchaVerify: (id: string, answer: string) => Promise<{ captchaToken: string; expiresAt: string }>;
  login: (input: LoginInput) => Promise<AuthTokensDto>;
  refresh: (refreshToken: string) => Promise<AuthTokensDto>;
  logout: (refreshToken: string) => Promise<void>;
  me: () => Promise<MeDto>;
  telegramStart: () => Promise<TelegramStartDto>;
  telegramStatus: (nonce: string, pollSecret: string) => Promise<TelegramStatusDto>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

/**
 * Klient in'ektsiya qilinadi (modul darajasidagi singleton emas) — shu tufayli
 * bu fayl hech kimni import qilmaydi va aylanma bog'liqlik hosil bo'lmaydi.
 */
export const createAuthApi = (client: ApiClient): AuthApi => ({
  captcha: async () => {
    const challenge = await client.apiFetch('/auth/captcha', { schema: captchaSchema });
    // Eski API muddatni aytmaydi — o'sha paytdagi TTL bilan davom etamiz.
    return { ...challenge, expiresIn: challenge.expiresIn ?? DEFAULT_CAPTCHA_TTL };
  },

  captchaVerify: async (id, answer) => {
    const ticket = await client.apiFetch('/auth/captcha/verify', {
      method: 'POST',
      body: JSON.stringify({ captchaId: id, captchaAnswer: answer }),
      schema: captchaTicketSchema,
    });
    // Nomi so'rovdagi maydon bilan bir xil bo'lishi uchun o'giriladi —
    // chaqiruvchi uni to'g'ridan-to'g'ri kirish so'roviga qo'ya oladi.
    return { captchaToken: ticket.token, expiresAt: ticket.expiresAt };
  },

  login: (input) =>
    client.apiFetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify(input),
      schema: authTokensSchema,
    }),

  refresh: (refreshToken) =>
    client.apiFetch('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
      schema: authTokensSchema,
    }),

  // 204 — tanasiz javob, shuning uchun sxema yo'q.
  logout: (refreshToken) =>
    client.apiFetch('/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken }) }),

  me: () => client.apiFetch('/auth/me', { schema: meSchema }),

  telegramStart: () =>
    client.apiFetch('/auth/telegram/start', {
      method: 'POST',
      body: JSON.stringify({}),
      schema: telegramStartSchema,
    }),

  /*
   * `POST` va `GET` emas: tanada sir (`pollSecret`) ketadi va `GET` da u
   * URL ga, u yerdan server jurnaliga va brauzer tarixiga tushib qolardi.
   */
  telegramStatus: (nonce, pollSecret) =>
    client.apiFetch('/auth/telegram/status', {
      method: 'POST',
      body: JSON.stringify({ nonce, pollSecret }),
      schema: telegramStatusSchema,
    }),

  changePassword: (currentPassword, newPassword) =>
    client.apiFetch('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
});
